import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import React, { useState } from "react";
import { logUserAction } from "../../utils/analytics";
import { useNavigate } from "react-router-dom";
import { colors } from "../../theme/theme";
import { NavigateBefore, NavigateNext } from "@mui/icons-material";

const getPercentage = (salePercentage) => {
  return (
    Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(salePercentage * 100) + "%"
  );
};

const SearchCategoryCard = ({ categoryCollection, search }) => {
  const navigate = useNavigate();

  const [currentImageIndex, setCurrentImageIndex] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);

  const smallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const mediumScreen = useMediaQuery((theme) =>
    theme.breakpoints.between("sm", "lg")
  );

  return (
    <Box sx={{ px: 2, pb: 2 }}>
      <Grid container spacing={2}>
        {categoryCollection.map((product, index) => {
          const images = product?.images || [product?.image_url];
          const currentIndex = currentImageIndex[product.product_id] || 0;
          const hasMultipleImages = images.length > 1;

          return (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <Card
                onClick={async () => {
                  await logUserAction({
                    action: "product_click",
                    data: {
                      product_id: product.product_id || null,
                      product_title: product.title || null,
                      brand: product.brand || null,
                    },
                    timestamp: new Date().toISOString(),
                  });

                  if (product.url) {
                    await logUserAction({
                      action: "checkout_redirect",
                      data: {
                        product_id: product.product_id || null,
                        redirect_url: product.url || null,
                      },
                      timestamp: new Date().toISOString(),
                    });

                    window.open(product.url, "_blank");
                  } else {
                    await logUserAction({
                      action: "product_click",
                      data: {
                        product_id: product.product_id || null,
                        product_title: product.title || null,
                        brand: product.brand || null,
                      },
                      timestamp: new Date().toISOString(),
                    });
                    setSelectedProduct(index);

                    navigate("/product", {
                      state: { product, searchQuery: search },
                    });
                  }
                }}
                sx={{
                  height: "100%",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "-1px 5px 5px 5px rgba(3, 2, 2, 0.07)",
                  position: "relative",
                  transition: "transform 0.2s",
                  border:
                    selectedProduct === index ? "2px solid #007BFF" : "none",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                }}
              >
                {product?.price?.sale_percentage != null && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "0px",
                      right: "0px",
                      backgroundColor: "#ABCC8C",
                      color: colors.textColor,
                      borderBottomLeftRadius: "20px",
                      padding: "5px 11px",
                      fontSize: smallScreen ? "8px" : "12px",
                      fontFamily: "Poppins-Bold",
                      zIndex: 2,
                    }}
                  >
                    {getPercentage(product?.price?.sale_percentage / 100)} Off
                  </Box>
                )}

                {product?.price?.original && product?.price?.discounted && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: "0px",
                      left: "0px",
                      backgroundColor: "#ff5252",
                      color: "white",
                      borderBottomRightRadius: "20px",
                      padding: "5px 11px",
                      fontSize: smallScreen ? "8px" : "12px",
                      fontFamily: "Poppins-Bold",
                      zIndex: 2,
                    }}
                  >
                    Save {product?.price?.original - product?.price?.discounted}{" "}
                    {product?.price?.currency}
                  </Box>
                )}

                <Box
                  sx={{
                    height: smallScreen ? "250px" : null,
                    aspectRatio: smallScreen ? "auto" : "1 / 1",
                    overflow: "hidden",
                    position: "relative",
                  }}
                >
                  <img
                    src={images[currentIndex]}
                    alt=""
                    onError={(e) =>
                      (e.target.src = require("../../assets/images/no_image.png"))
                    }
                    style={{
                      objectFit: "contain",
                      width: "100%",
                      height: "100%",
                    }}
                  />

                  {hasMultipleImages && (
                    <>
                      <IconButton
                        sx={{
                          position: "absolute",
                          left: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          backgroundColor: "rgba(0,0,0,0.5)",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.7)",
                          },
                          zIndex: 2,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex((prev) => ({
                            ...prev,
                            [product.product_id]:
                              ((prev[product.product_id] || 0) -
                                1 +
                                images.length) %
                              images.length,
                          }));
                        }}
                      >
                        <NavigateBefore />
                      </IconButton>
                      <IconButton
                        sx={{
                          position: "absolute",
                          right: 8,
                          top: "50%",
                          transform: "translateY(-50%)",
                          backgroundColor: "rgba(0,0,0,0.5)",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.7)",
                          },
                          zIndex: 2,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setCurrentImageIndex((prev) => ({
                            ...prev,
                            [product.product_id]:
                              ((prev[product.product_id] || 0) + 1) %
                              images.length,
                          }));
                        }}
                      >
                        <NavigateNext />
                      </IconButton>
                      <Box
                        onClick={(e) => {}}
                        sx={{
                          position: "absolute",
                          bottom: 8,
                          left: 0,
                          right: 0,
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                          zIndex: 2,
                        }}
                      >
                        {images.map((_, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              backgroundColor:
                                currentIndex === idx
                                  ? "white"
                                  : "rgba(255,255,255,0.5)",
                            }}
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </Box>

                <CardContent
                  sx={{
                    px: 1.5,
                    pt: 1.5,
                    backgroundColor: "#fff",
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    "&:last-child": {
                      pb: 0.5,
                    },
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: "#9AE0EB",
                      borderRadius: "4px",
                      padding: "4px 8px",
                      width: "fit-content",
                      minWidth: "fit-content",
                      mb: 1,
                      display: "flex",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ fontSize: "0.7rem", fontWeight: "bold" }}
                    >
                      {product?.brand}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.textColor2,
                      fontWeight: "bold",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mb: 0.5,
                    }}
                  >
                    {product?.title}
                  </Typography>

                  {/* Price Display Logic */}
                  {product?.price?.discounted ? (
                    // If there's a discounted price, show original as struck through
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#999",
                          textDecoration: "line-through",
                          fontSize: "0.8rem",
                        }}
                      >
                        {product?.price?.original}{" "}
                        {product?.price?.currency || ""}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.textColor2,
                          fontWeight: "bold",
                          mb: 1,
                        }}
                      >
                        {product?.price?.discounted}{" "}
                        {product?.price?.currency || ""}
                      </Typography>
                    </>
                  ) : (
                    // If no discounted price, show original as main price
                    product?.price?.original && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.textColor2,
                          fontWeight: "bold",
                          mb: 1,
                        }}
                      >
                        {product?.price?.original}{" "}
                        {product?.price?.currency || ""}
                      </Typography>
                    )
                  )}

                  {(product?.description || product?.enhanced_description) && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: colors.textColor2,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        // mb: 1,
                      }}
                    >
                      {product?.description || product?.enhanced_description}
                    </Typography>
                  )}

                  {/* {product?.variants?.length > 0 && (
                        <Box sx={{ mt: "auto" }}>
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: "bold" }}
                          >
                            Sizes:
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.5,
                              mt: 0.5,
                            }}
                          >
                            {product.variants.map((variant, i) => (
                              <Chip
                                key={i}
                                label={variant.size}
                                size="small"
                                sx={{
                                  fontSize: "0.6rem",
                                  height: "20px",
                                  backgroundColor: "#e0e0e0",
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      )} */}

                  {/* <Stack
                        direction="row"
                        sx={{
                          mt: "auto",
                          alignItems: "center",
                        }}
                      >
                        <StoreOutlined
                          sx={{ color: "#1B1211", fontSize: "14px" }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#1B1211",
                            ml: 0.5,
                            fontSize: "0.7rem",
                          }}
                        >
                          shop no. 504 3rd floor luckyone mall
                        </Typography>
                      </Stack> */}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default SearchCategoryCard;
